/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : C4 / Communication Management §9 — Provider Web token picker
 * Purpose      : Browser-side MIRROR of the one server token catalog.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * C4             | 01-Aug-2026 | Vijayakumar S  | Initial creation — mirrors
 *                                                 CommunicationToken.java verbatim
 * Comms token    | 05-Aug-2026 | Claude         | Mirror the 20-token expansion (new PROJECT
 * expansion                                       group; Client/Company/Estimate grown) —
 *                                                 see CommunicationToken.java's own revision
 *                                                 history for what was deliberately left out.
 */

/**
 * ============================ READ THIS BEFORE EDITING ============================
 *
 * This file is a HAND-SYNCHRONISED MIRROR of the single server-side catalog:
 *
 *     src/api/shared/gmscloud-common/gmscloud-shared/src/main/java/
 *         com/gmscloud/shared/enums/Communication/CommunicationToken.java
 *
 * It is NOT a new hand-maintained list, and it must never become one. The whole point
 * of C4 was to collapse three divergent lists into one; a mirror that drifts recreates
 * the exact defect C4 removed.
 *
 * WHY A MIRROR AND NOT A FETCH — this is a recorded gap, not a design choice.
 * ---------------------------------------------------------------------------------
 * `CommunicationToken` is a Java enum with NO REST endpoint. Nothing in
 * `provider/gmscloud-provider-api/.../server/api/` exposes it, and this task was
 * scoped out of `src/api`, so no endpoint could be added here. The browser therefore
 * has no way to READ the catalog at runtime.
 *
 * THE FIX, when provider-api can be touched: add
 *
 *     GET /api/v1/communication-tokens   ->  [{ tokenKey, group, label, placeholder }]
 *
 * backed by `CommunicationToken.byGroup()` (which already returns exactly the shape a
 * picker wants), then delete the COMMUNICATION_TOKENS constant below and have
 * `TokenPickerSource` fetch it. Everything else in this feature keeps working —
 * consumers already depend on the accessor functions, not on the array.
 *
 * UNTIL THEN: `CommunicationTokenCatalog.test.ts` pins this mirror to the Java enum,
 * asserting the exact keys, their order, their groups, and — explicitly — that the
 * invented token (`rep.name`) is absent. If someone adds a token to the Java enum and
 * not here, or vice versa, that test is the tripwire.
 *
 * INCLUSION RULE (copied from the Java catalog): a token belongs here only if some
 * send-time resolver actually populates it. The authority is
 * `ProjectService#buildEstimateSmsContext`. Offering a token that resolves to nothing
 * means the body saves clean and the SEND fails in production.
 *
 * Deliberately EXCLUDED — previously offered by a UI list, resolves to nothing:
 *   owner.phone, review.link                (list 1 — no resolver)
 *   rep.name                                (list 2 — invented namespace)
 * (payment.link and company.phone were once excluded here too; both now have send-time resolvers
 *  — payment.link via InvoiceService#buildPaymentSmsContext / Project#resolveInvoicePaymentLink,
 *  company.phone via the 2026-08-05 expansion — so both are catalogued, not excluded.)
 *
 * NOTE ON §9's EXAMPLE: the design's `{{customer.firstName}}` is NOT a real token.
 * The live namespace is `client.*` / `estimate.amount`. `gmscloud-customer-data` is
 * not on provider-api's classpath, so `customer.*` cannot resolve in this app at all.
 * It is not offered here, and inventing it to satisfy the example would ship a picker
 * whose output silently renders to nothing.
 * ==================================================================================
 */

/** Sections the picker renders, in the Java enum's declaration order. */
export type TokenGroup = 'CLIENT' | 'COMPANY' | 'OWNER' | 'PROJECT' | 'ESTIMATE' | 'INVOICE' | 'PAYMENT'
  | 'CONTRACT';

export interface CommunicationTokenDef {
  /** Bare dotted name as it appears between the braces, e.g. `client.firstName`. */
  tokenKey: string;
  group: TokenGroup;
  /** Human-readable name for the authoring UI. */
  label: string;
}

/** Group display labels — mirrors `CommunicationToken.TokenGroup.getLabel()`. */
export const TOKEN_GROUP_LABELS: Record<TokenGroup, string> = {
  CLIENT: 'Client',
  COMPANY: 'Company',
  OWNER: 'Owner',
  PROJECT: 'Project',
  ESTIMATE: 'Estimate',
  INVOICE: 'Invoice',
  PAYMENT: 'Payment',
  CONTRACT: 'Contract',
};

/**
 * Mirror of `CommunicationToken.values()`, in declaration order.
 * DO NOT add an entry here without adding it to the Java enum first.
 */
export const COMMUNICATION_TOKENS: readonly CommunicationTokenDef[] = Object.freeze([
  { tokenKey: 'client.firstName', group: 'CLIENT', label: 'Client first name' },
  { tokenKey: 'client.lastName', group: 'CLIENT', label: 'Client last name' },
  { tokenKey: 'client.fullName', group: 'CLIENT', label: 'Client full name' },
  { tokenKey: 'client.address', group: 'CLIENT', label: 'Client address' },
  { tokenKey: 'client.city', group: 'CLIENT', label: 'Client city' },
  { tokenKey: 'client.phone', group: 'CLIENT', label: 'Client phone number' },
  { tokenKey: 'client.state', group: 'CLIENT', label: 'Client state' },
  { tokenKey: 'client.country', group: 'CLIENT', label: 'Client country' },
  { tokenKey: 'client.email', group: 'CLIENT', label: 'Client email' },

  { tokenKey: 'company.name', group: 'COMPANY', label: 'Company name' },
  { tokenKey: 'company.address', group: 'COMPANY', label: 'Company address' },
  { tokenKey: 'company.city', group: 'COMPANY', label: 'Company city' },
  { tokenKey: 'company.state', group: 'COMPANY', label: 'Company state' },
  { tokenKey: 'company.country', group: 'COMPANY', label: 'Company country' },
  { tokenKey: 'company.phone', group: 'COMPANY', label: 'Company phone number' },
  { tokenKey: 'company.email', group: 'COMPANY', label: 'Company email' },

  { tokenKey: 'owner.name', group: 'OWNER', label: 'Project owner name' },

  { tokenKey: 'project.name', group: 'PROJECT', label: 'Project name' },
  { tokenKey: 'project.orderNumber', group: 'PROJECT', label: 'Project order number' },
  { tokenKey: 'project.estimateNumber', group: 'PROJECT', label: 'Estimate number' },
  { tokenKey: 'project.trade', group: 'PROJECT', label: 'Project trade' },
  { tokenKey: 'project.status', group: 'PROJECT', label: 'Project status' },
  { tokenKey: 'project.startDate', group: 'PROJECT', label: 'Project start date' },
  { tokenKey: 'project.createdDate', group: 'PROJECT', label: 'Project created date' },
  { tokenKey: 'project.endDate', group: 'PROJECT', label: 'Project end date' },

  { tokenKey: 'estimate.amount', group: 'ESTIMATE', label: 'Estimate total amount' },
  { tokenKey: 'estimate.link', group: 'ESTIMATE', label: 'Estimate preview link' },
  { tokenKey: 'estimate.description', group: 'ESTIMATE', label: 'Estimate description' },
  { tokenKey: 'estimate.subtotal', group: 'ESTIMATE', label: 'Estimate subtotal' },
  { tokenKey: 'estimate.retainageAmount', group: 'ESTIMATE', label: 'Estimate retainage amount' },

  { tokenKey: 'invoice.number', group: 'INVOICE', label: 'Invoice number' },
  { tokenKey: 'invoice.amount', group: 'INVOICE', label: 'Invoice amount' },
  { tokenKey: 'invoice.tax', group: 'INVOICE', label: 'Invoice tax' },
  { tokenKey: 'invoice.dueDate', group: 'INVOICE', label: 'Invoice due date' },
  { tokenKey: 'invoice.status', group: 'INVOICE', label: 'Invoice status' },
  { tokenKey: 'invoice.issueDate', group: 'INVOICE', label: 'Invoice issue date' },
  { tokenKey: 'invoice.term', group: 'INVOICE', label: 'Invoice term' },

  { tokenKey: 'payment.link', group: 'PAYMENT', label: 'Payment link' },

  // CONTRACT — the contract email templates (CONTRACT_DRAFT/SENT/CONFIRMED) are authored through
  // this same editor, and `contract.link` is the token that carries the signing URL. It was
  // catalogued backend-side with the CONTRACT group but never mirrored here, so a provider admin
  // editing "Contract - Sent to Customer" could see the token in the body yet had no way to insert
  // it back if it were removed — the picker simply did not offer it.
  //
  // Unlike every other token here, this one has NO resolver: it is supplied by the caller at send
  // time, which is why ContractTemplateResolver's required-token gate exists to verify it actually
  // arrived before the mail goes out.
  { tokenKey: 'contract.link', group: 'CONTRACT', label: 'Contract signing link' },
] as const);

/** The token as written in a body — mirrors `CommunicationToken.getPlaceholder()`. */
export const placeholderFor = (tokenKey: string): string => `{{${tokenKey}}}`;

/** Every catalogued bare key, in declaration order — mirrors `allKeys()`. */
export const allTokenKeys = (): string[] => COMMUNICATION_TOKENS.map((t) => t.tokenKey);

/** Whether a bare dotted name is catalogued — mirrors `isKnown()`. Never throws. */
export const isKnownToken = (tokenKey: string | null | undefined): boolean =>
  tokenKey != null && COMMUNICATION_TOKENS.some((t) => t.tokenKey === tokenKey);

/** Lookup by bare dotted name — mirrors `fromKey()`. Undefined when not catalogued. */
export const tokenByKey = (tokenKey: string | null | undefined): CommunicationTokenDef | undefined =>
  tokenKey == null ? undefined : COMMUNICATION_TOKENS.find((t) => t.tokenKey === tokenKey);

/** Catalogue sectioned by group, declaration order preserved — mirrors `byGroup()`. */
export const tokensByGroup = (): Array<{ group: TokenGroup; label: string; tokens: CommunicationTokenDef[] }> => {
  const order: TokenGroup[] = [];
  const buckets = new Map<TokenGroup, CommunicationTokenDef[]>();
  COMMUNICATION_TOKENS.forEach((token) => {
    if (!buckets.has(token.group)) {
      buckets.set(token.group, []);
      order.push(token.group);
    }
    buckets.get(token.group)!.push(token);
  });
  return order.map((group) => ({
    group,
    label: TOKEN_GROUP_LABELS[group],
    tokens: buckets.get(group)!,
  }));
};
